import { AfterViewInit, ChangeDetectorRef, DestroyRef, Injectable, Input, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { FileUploadService } from '../../../shared/components/file-upload/file-upload.service';
import { UploadFile } from '../../models/upload-file.model';
import { Filter, JwtPayload, PaginateQuery, PaginateResult } from '../../models';
import { ApiService } from '../../../services';
import { ErrorHandlingService, TokenService } from '../../services';
import { ExportFileTypeEnum } from '../../../shared/enums/export-file.enum';
import { normalizeSearchValueForPhone } from '../../../functions/phone-filter.function';

@Injectable({
  providedIn: 'root',
})
export abstract class ListComponent implements AfterViewInit {
  @Input()
  manySelectable: boolean = false;

  @Input()
  singleSelectable: boolean = false;

  public selectedRowsData: any[] = [];

  dataGrid!: Table;
  selectedRows = [];
  fields: string[] = [];
  popupVisibleConfirm = false;
  popupVisibleMessage = false;
  paginateQuery = { skip: 0, take: 10, orderBy: {} } as PaginateQuery;
  sortField!: string;
  searchControl = new FormControl();
  data: PaginateResult = { list: [], total: 0 };
  rowsPerPage = [10, 20, 50];
  destroyRef = inject(DestroyRef);
  errorHandlingService = inject(ErrorHandlingService);
  changeDetector = inject(ChangeDetectorRef);
  toastr = inject(ToastrService);
  fileUploadService = inject(FileUploadService);
  tokenService = inject(TokenService);
  jwtPayload = {} as JwtPayload;
  formGroup!: FormGroup;
  isDashboardCustomer = input<boolean>(false) as any;
  fieldsReport: any[] = [];
  lastEvent: TableLazyLoadEvent = {};
  loading = signal(true);
  skip = 0;

  constructor(
    public service: ApiService,
    public router: Router,
    public defaultSort: { [key: string]: string },
    public confirmationService: ConfirmationService,
    public messageService: MessageService,
  ) {}

  private get searchKey(): string {
    const base = this.router.url?.split('?')[0] ?? '';
    return `search:${base}`;
  }

  public get tableStateKey(): string {
    const base = this.router.url?.split('?')[0] ?? '';
    const componentName = this.constructor.name ?? 'ListComponent';
    return `tableState:${base}:${componentName}`;
  }

  ngOnInit() {
    this.getJwtPayload();
    const saved = sessionStorage.getItem(this.searchKey);
    if (saved != null) {
      try {
        const value = JSON.parse(saved);
        this.searchControl.setValue(value, { emitEvent: false });
        const normalized = this.normalizeSearchValue(value);
        this.paginateQuery.search = (normalized == null || normalized === '') ? undefined : normalized;
      } catch {
        // noop
      }
    }
  }

  ngAfterViewInit(): void {
    this.search();
  }

  load(event: TableLazyLoadEvent) {
    this.loading.set(true);
    const { total } = this.data;
    this.data = { total, list: [] };
    this.lastEvent = event;
    const { first, sortField, sortOrder, rows } = event;
    this.skip = first ?? 0;
    const sort = sortField ? { [sortField as string]: sortOrder === 1 ? 'ASC' : 'DESC' } : this.defaultSort;
    const id = this.jwtPayload?.CustomerID;
    if (id) {
      const defaultFilter: Filter[] | undefined = this.getDefaultFilters(id, this.fields, 3, 'AND');

      if (defaultFilter && defaultFilter.length > 0) {
        this.paginateQuery.filters ??= [];

        const missingFilters = defaultFilter.filter(
          (df) => !this.paginateQuery.filters?.some((f) => f.field === df.field && f.operation === df.operation && f.value === df.value),
        );

        if (missingFilters.length > 0) {
          this.paginateQuery.filters.push(...missingFilters);
        }
      }
    }

    this.sortField = sortField as string;
    this.paginateQuery.skip = this.skip;
    this.paginateQuery.orderBy = sort;
    this.paginateQuery.take = rows ?? 10;
    return this.service
      .paginate(this.paginateQuery)
      .pipe(
        catchError((error) => {
          this.loading.set(false);
          return this.errorHandlingService.handleError(error, 'Error retrieving data.');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.data = result;
        this.replaceNullOrEmptyValues(this.data);
        this.loading.set(false);
        this.changeDetector.markForCheck();
      });
  }

  exportData(exportFileType: ExportFileTypeEnum) {
    const paginateQuery = JSON.parse(JSON.stringify(this.paginateQuery));
    paginateQuery.skip = 0;
    paginateQuery.take = this.data.total;

    return this.service
      .export({ paginateQuery: paginateQuery, exportFileType })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error, 'Error retrieving data.')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.toastr.info(result as string, 'Confirmed');
      });
  }

  confirmDelete(event: Event, row: any, id: string, index?: number): void {
    if (!this.hasPermissionDelete()) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to delete this record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text focus:border-none',

      accept: () => {
        this.delete(row[id], index);
      },
      reject: () => {},
    });
  }

  download(file: UploadFile) {
    this.fileUploadService
      .getDownloadRequest(file)
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error, 'Error downloading file')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const blob = new Blob([response], { type: file.documentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.documentName;
        a.click();
      });
  }

  delete(id: string, index?: number): void {
    this.service
      .delete(id)
      .pipe(catchError((error) => this.errorHandlingService.handleError(error, 'An error occurred while deleting the data. Please try again later.')))
      .subscribe(() => {
        this.toastr.info('Record deleted', 'Confirmed');
        this.fetchData();
      });
  }

  new(): void {
    if (!this.hasPermissionEdit()) {
      return;
    }
    const path = `${this.router.url}/new`;
    this.router.navigateByUrl(path);
  }

  edit(row: any, id: string): void {
    const url = `${this.router.url}/edit/${row[id]}`;
    this.router.navigateByUrl(url);
  }

  hasPermissionEdit() {
    return this.tokenService.hasPermission(this.service.endPoint + 'Create');
  }

  hasPermissionDelete() {
    return this.tokenService.hasPermission(this.service.endPoint + 'Delete');
  }

  /**
   * Prepares the typed search term before it is sent to the paginate endpoint.
   * Lists whose endpoint normalizes phone masks itself should override this and send the raw term.
   */
  protected normalizeSearchValue(value: string | null | undefined): string | null | undefined {
    return normalizeSearchValueForPhone(value);
  }

  search(): void {
    if (!this.isDashboardCustomer()) {
      this.searchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap((value) => {
            if (value == null || value === '') {
              sessionStorage.removeItem(this.searchKey);
            } else {
              sessionStorage.setItem(this.searchKey, JSON.stringify(value));
            }
            const normalized = this.normalizeSearchValue(value);
            this.paginateQuery.search = (normalized == null || normalized === '') ? undefined : normalized;
          }),
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => {
            return of(this.fetchData());
          }),
        )
        .subscribe();
      return;
    }
    this.filtersChanges();
  }

  fetchData() {
    const sortOrder = this.paginateQuery.orderBy![this.sortField] === 'ASC' ? 1 : -1;
    return this.load({
      first: this.skip,
      rows: this.paginateQuery.take,
      filters: this.paginateQuery.filters as any,
      sortOrder,
      sortField: this.sortField,
    });
  }

  getDefaultFilters = (searchTxt: string, fields: string[], operation = 1, condition = 'OR'): Filter[] => {
    const filters = [];
    for (const field of fields) {
      filters.push({
        field,
        operation,
        value: searchTxt,
        condition,
      });
    }
    return filters as Filter[];
  };

  getDefaultFiltersToReport() {
    const filters = [];
    for (const item of this.fieldsReport) {
      const value = this.formGroup.controls[item.key].value;
      if (value) {
        filters.push({
          field: item.filed,
          operation: item.operation,
          value,
          condition: 'AND',
        });
      }
    }
    return filters as Filter[];
  }

  getJwtPayload() {
    this.jwtPayload = this.tokenService.getJwtPayload();
  }

  filtersChanges() {
    this.formGroup.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.paginateQuery.filters = this.getDefaultFiltersToReport();
          return of(this.fetchData());
        }),
      )
      .subscribe();
  }

  refreshFilter(filters: Filter[]) {
    this.paginateQuery.filters = filters;
    this.load(this.lastEvent);
  }

  replaceNullOrEmptyValues(data: PaginateResult) {
    data.list?.forEach((item) => {
      Object.keys(item).forEach((key) => {
        item[key] = this.getDefaultValue(item[key]);
      });
    });
  }

  getDefaultValue(value: string | boolean | Date | undefined | number) {
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '' && !Array.isArray(value))) {
      return '--';
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? '--' : value.join(', ');
    }
    return value;
  }

}
